import React, { useState, useEffect, useMemo } from "react";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useSelector } from "react-redux";
// CUSTOM HOOKS
import usePostCommand from "../../../customHooks/POST_requests/usePostCommand";
import useGetAllMessages from "../../../customHooks/GET_requests/useGetAllMessages";
// COMPONENTS
import ActionCommandButton from "../Actions/components/ActionCommandButton";
// INTERFACES
import { RootState } from "../../../app/store";
import { ActionCommandOption } from "../../../interfaces/Bank.interface";
// CONSTANTS
import {
  resolveCommandEnum,
  resolveValidStateEnums,
  assertBoundCommand,
  UNBOUND_COMMAND_REASON,
  UNBOUND_PAIR_REASON,
  dispatchFloor,
  floorSelectionMissing,
} from "./actionButtonUtils";
import useAwaitingTargetReason from "./useAwaitingTargetReason";
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";
// VERIFICATION
import useActionOutcome, { ActionVerification } from "./useActionOutcome";

export default function ActionButtonTimed({
  validStates,
  label,
  toggleLabel,
  messageId,
  toggleMessageId,
  command = null,
  toggleCommand = null,
  colorAction,
  timerAction,
  carService,
  targetFloor = undefined,
  expectedResponse = null,
  expectedResponseTimeout = null,
  commandMessageId = null,
  disableButton,
  setDisableButton,
}: {
  validStates: string[];
  label: string;
  toggleLabel: string;
  messageId: string;
  toggleMessageId: string;
  command?: ActionCommandOption | null;
  toggleCommand?: ActionCommandOption | null;
  colorAction: string;
  timerAction: number;
  carService: number;
  /**
   * The floor this row's commands dispatch to. `undefined` for a command that
   * takes no floor (dispatches at 0, as before); `null` while a floor-taking
   * command is still waiting for the operator to pick one. See dispatchFloor.
   */
  targetFloor?: number | null;
  disableButton: boolean;
  setDisableButton: (newValue: boolean) => void;
} & ActionVerification) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [loading, setLoading] = useState(false);
  const [counter, setCounter] = useState(0); // Initial counter value
  const [localCarService, setLocalCarService] = useState<number>(0);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const { triggerPostCommand, isLoadingPostCommand } = usePostCommand();
  const { messages } = useGetAllMessages();
  const reportOutcome = useActionOutcome({
    expectedResponse,
    expectedResponseTimeout,
    commandMessageId,
  });

  // ************************************************ */
  // MEMOIZED VALUES ******************************** */
  // null (not 0) when the message is unbound — enum 0 is a real Control
  // (CarCallLock), so a 0 fallback would mis-fire that command.
  const messageEnum = useMemo(
    () => resolveCommandEnum(messages, messageId, command),
    [messages, messageId, command],
  );

  const toggleMessageEnum = useMemo(
    () => resolveCommandEnum(messages, toggleMessageId, toggleCommand),
    [messages, toggleMessageId, toggleCommand],
  );

  const validStatesNumbers = useMemo(
    () => resolveValidStateEnums(messages, validStates),
    [messages, validStates],
  );

  // ************************************************ */
  // FUNCTIONS ************************************** */
  // Send the action's BOUND command, never a hardcoded Control value.
  const launchTimed = () => {
    setLoading(true);
    let count = timerAction;
    setDisableButton(true);
    setCounter(count);
    const countdownInterval = setInterval(() => {
      count -= 1;
      setCounter(count);
      if (count === 0) {
        setDisableButton(false);
        clearInterval(countdownInterval);
        setLoading(false);
      }
    }, 1000);
    setTimeout(async () => {
      clearInterval(countdownInterval);
      setLoading(false);
      // The group-wide TURNBACK lock is released here as well as by the
      // countdown: the countdown only reaches 0 if the interval was allowed to
      // run every tick, and a backgrounded tab throttles it. Leaving the lock
      // set would freeze every TURNBACK row on the panel.
      setDisableButton(false);
      if (!assertBoundCommand(toggleMessageEnum, toggleLabel, theme)) {
        reportOutcome(toggleLabel, false, toggleMessageId ?? null);
        return;
      }
      const delivered = await triggerPostCommand({
        floor: dispatchFloor(targetFloor),
        command: toggleMessageEnum,
        liftType: LiftType[LiftType.ELEVATOR] as "ELEVATOR",
        active: true,
      });
      reportOutcome(toggleLabel, delivered, toggleMessageId ?? null);
    }, timerAction * 1000); // Stop countdown and loading indicator after the timer elapses
  };

  // The turn-back SEQUENCE starts on delivery, not on `isSuccessPostCommand`.
  // That flag belongs to the EMULATOR mutation and is permanently false on the
  // EDGE and OFFLINE paths, so on every production install the countdown never
  // ran and the paired command — the whole point of a TURNBACK — was never sent.
  const onClickTimedButton = async () => {
    if (!assertBoundCommand(messageEnum, label, theme)) {
      reportOutcome(label, false, messageId);
      return;
    }
    const delivered = await triggerPostCommand({
      floor: dispatchFloor(targetFloor),
      command: messageEnum,
      liftType: LiftType[LiftType.ELEVATOR] as "ELEVATOR",
      active: true,
    });
    reportOutcome(label, delivered, messageId);
    if (delivered) launchTimed();
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    setLocalCarService(carService);
  }, [carService]);

  // ************************************************ */
  // COMPONENT ************************************** */
  // The click sends the primary and the paired command follows automatically
  // once the timer elapses. `disableButton` is the group-wide TURNBACK lock
  // owned by the parent — it deliberately blocks every TURNBACK row while one
  // is running, and that stays as it was.
  const blocked = validStatesNumbers.includes(localCarService);
  const busy = loading || isLoadingPostCommand || disableButton;

  // A floor-taking command must not dispatch until the operator picks a floor
  // on the row's strip; `targetFloor` is null for exactly as long as that is
  // true. The gate is VISIBLE — a disabled button naming its reason — rather
  // than a click that silently sends nothing, and rather than the position-0
  // dispatch it replaces, which aimed a positional command at a floor the
  // operator never chose.
  const awaitingFloor = floorSelectionMissing(targetFloor);
  const selectFloorReason = useAwaitingTargetReason(messageEnum);
  // A function rather than a ternary at the call site: the floor gate outranks
  // the service-state gate, and both have to reach one tooltip slot without
  // nesting a conditional inside a conditional.
  // Both legs must resolve before the primary may be pressed: the primary's
  // own press is what dispatches the pair, so a missing pair could only be
  // reported after the primary had already gone out.
  const pairUnbound = messageEnum == null || toggleMessageEnum == null;
  const primaryReason = (): string | undefined => {
    if (pairUnbound) return UNBOUND_PAIR_REASON;
    if (awaitingFloor) {
      return selectFloorReason;
    }
    if (blocked) {
      return t(
        "monitoringActions.unavailableInCurrentState",
        "Unavailable in the current service mode",
      );
    }
    return undefined;
  };

  return (
    <>
      <ActionCommandButton
        label={label}
        color={colorAction}
        loading={busy}
        disabled={pairUnbound || blocked || awaitingFloor || busy}
        disabledReason={primaryReason()}
        onClick={onClickTimedButton}
      />
      {/* The paired command has no operator control — the timer sends it — but
          it is still one of the two commands this row runs, so it gets a button
          that is permanently inert rather than no button at all. Hiding it made
          a TURNBACK look like a single-command action, and the countdown that
          used to replace the PRIMARY button's label read as though the primary
          were about to fire again. The countdown belongs here, on the command
          that is actually pending. */}
      <ActionCommandButton
        label={toggleLabel}
        countdown={loading ? counter : null}
        disabled
        disabledReason={
          toggleMessageEnum == null
            ? UNBOUND_COMMAND_REASON
            : t("monitoringActions.sentAutomatically", "Sent automatically when the countdown ends")
        }
      />
    </>
  );
}
